// Sidebar Navigation Component

import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { userManagementService } from '../api/userManagementService';
import { clubNewsService } from '../api/clubNewsService';
import { clubEventsService } from '../api/clubEventsService';
import { SubMenuItem, filterMenuItems as filterMenuItemsByPermissions } from '../config/navigation';
import './Sidebar.css';

// "Neu seit"-Badges: lokal pro Browser/User in localStorage gespeichert.
// Beim ersten Aufruf wird "jetzt" als Baseline gesetzt, damit nicht der gesamte
// Historien-Bestand als "neu" markiert wird.
const LAST_SEEN_PREFIX = 'sidebar:lastSeen:';
const NEW_BADGE_POLL_MS = 60_000;

function getLastSeen(key: string): number {
  try {
    const raw = localStorage.getItem(LAST_SEEN_PREFIX + key);
    if (raw) return Number(raw);
    const now = Date.now();
    localStorage.setItem(LAST_SEEN_PREFIX + key, String(now));
    return now;
  } catch {
    return Date.now();
  }
}

function setLastSeen(key: string, ts: number) {
  try { localStorage.setItem(LAST_SEEN_PREFIX + key, String(ts)); } catch { /* ignore */ }
}

export function Sidebar() {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [newCounts, setNewCounts] = useState<Record<'news' | 'events', number>>({ news: 0, events: 0 });
  const location = useLocation();

  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const permissions = await userManagementService.getMyPermissions();
        console.log('Geladene Berechtigungen:', permissions);
        setUserPermissions(permissions);
      } catch (err) {
        console.error('Fehler beim Laden der Berechtigungen:', err);
        // If error, show all menu items (fallback)
        setUserPermissions(['*']); // Special marker to show all
      } finally {
        setLoading(false);
      }
    };

    loadPermissions();
  }, []);

  // "Neu seit letztem Besuch"-Zähler periodisch refreshen
  const refreshNewCounts = useCallback(async () => {
    const newsSince = getLastSeen('news');
    const eventsSince = getLastSeen('events');
    try {
      const [posts, events] = await Promise.all([
        clubNewsService.getPosts().catch(() => []),
        clubEventsService.getEvents(undefined, undefined, true).catch(() => []),
      ]);
      const newsCount = posts.filter(p => new Date(p.createdAt).getTime() > newsSince).length;
      const eventsCount = events.filter(e => new Date(e.createdAt).getTime() > eventsSince).length;
      setNewCounts({ news: newsCount, events: eventsCount });
    } catch { /* ignore — keine Berechtigung o. ä. */ }
  }, []);

  useEffect(() => {
    refreshNewCounts();
    const handle = setInterval(refreshNewCounts, NEW_BADGE_POLL_MS);
    return () => clearInterval(handle);
  }, [refreshNewCounts]);

  // Beim Navigieren auf eine getrackte Seite: lastSeen-Timestamp aktualisieren + Badge ausblenden
  useEffect(() => {
    if (location.pathname === '/club-app/beitraege') {
      setLastSeen('news', Date.now());
      setNewCounts(c => ({ ...c, news: 0 }));
    } else if (location.pathname === '/club-app/termine') {
      setLastSeen('events', Date.now());
      setNewCounts(c => ({ ...c, events: 0 }));
    }
  }, [location.pathname]);

  const toggleExpand = (label: string) => {
    setExpandedItems((prev) =>
      prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]
    );
  };

  const isExpanded = (label: string) => expandedItems.includes(label);

  const isSubItemActive = (subItems?: SubMenuItem[]) => {
    if (!subItems) return false;
    return subItems.some((sub) => location.pathname === sub.path);
  };

  // Filterung + Menüdefinition liegen zentral in ../config/navigation

  const visibleMenuItems = filterMenuItemsByPermissions(userPermissions);

  const badgeFor = (key?: 'news' | 'events'): number => key ? newCounts[key] : 0;
  // Summe aller Sub-Badges in einer Gruppe → Anzeige am eingeklappten Gruppen-Label.
  const groupBadgeTotal = (subItems?: SubMenuItem[]): number =>
    (subItems || []).reduce((sum, s) => sum + badgeFor(s.newBadgeKey), 0);

  if (loading) {
    return (
      <aside className="sidebar">
        <div style={{ padding: '20px', color: 'rgba(255, 255, 255, 0.7)' }}>
          Lade Menü...
        </div>
      </aside>
    );
  }

  return (
    <aside className="sidebar">

      <nav className="sidebar-nav">
        {visibleMenuItems.map((item) =>
          item.subItems ? (
            <div key={item.label} className="nav-item-group">
              <button
                className={`nav-item nav-item-expandable ${
                  isSubItemActive(item.subItems) ? 'active' : ''
                }`}
                onClick={() => toggleExpand(item.label)}
              >
                <span className="nav-icon">{item.icon}</span>
                <span className="nav-label">{item.label}</span>
                {!isExpanded(item.label) && groupBadgeTotal(item.subItems) > 0 && (
                  <span className="nav-badge">{groupBadgeTotal(item.subItems)}</span>
                )}
                <span className={`nav-arrow ${isExpanded(item.label) ? 'expanded' : ''}`}>
                  ▼
                </span>
              </button>
              {isExpanded(item.label) && (
                <div className="sub-menu">
                  {item.subItems.map((subItem) => {
                    const count = badgeFor(subItem.newBadgeKey);
                    return (
                      <NavLink
                        key={subItem.path}
                        to={subItem.path}
                        className={({ isActive }) => `sub-nav-item ${isActive ? 'active' : ''}`}
                      >
                        <span className="sub-nav-label">{subItem.label}</span>
                        {count > 0 && <span className="nav-badge">{count}</span>}
                      </NavLink>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <NavLink
              key={item.path}
              to={item.path!}
              end={item.path !== '/website'}
              className={({ isActive }) => {
                const active = isActive || (item.path === '/website' && location.pathname.startsWith('/website'));
                return `nav-item ${active ? 'active' : ''}`;
              }}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
            </NavLink>
          )
        )}
      </nav>
    </aside>
  );
}
